// Wrapper GitHub Actions REST API — scoped to LucasCTSW/onpitch
// Requires REACT_APP_GITHUB_TOKEN (Fine-grained PAT, Actions R/W)

const GITHUB_REPO = 'LucasCTSW/onpitch';
const API_BASE = `https://api.github.com/repos/${GITHUB_REPO}`;

export const GITHUB_TOKEN: string = process.env.REACT_APP_GITHUB_TOKEN || '';
export const hasGitHubToken = (): boolean => GITHUB_TOKEN.length > 0;

export interface WorkflowRun {
  id: number;
  name: string;
  status: string;            // queued | in_progress | completed
  conclusion: string | null; // success | failure | cancelled | null
  created_at: string;
  updated_at: string;
  run_started_at: string | null;
  html_url: string;
  event: string;
  run_number: number;
}

interface RunsResponse {
  total_count: number;
  workflow_runs: WorkflowRun[];
}

function assertToken() {
  if (!GITHUB_TOKEN) {
    throw new Error('REACT_APP_GITHUB_TOKEN manquant — configure-le dans admin/.env');
  }
}

async function ghFetch(path: string, init: RequestInit = {}): Promise<Response> {
  assertToken();
  return fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${GITHUB_TOKEN}`,
      Accept: 'application/vnd.github+json',
      'X-GitHub-Api-Version': '2022-11-28',
      'Content-Type': 'application/json',
      ...(init.headers || {}),
    },
  });
}

export async function dispatchWorkflow(
  workflowFile: string,
  inputs: Record<string, string> = {},
  ref: string = 'main',
): Promise<void> {
  const res = await ghFetch(`/actions/workflows/${workflowFile}/dispatches`, {
    method: 'POST',
    body: JSON.stringify({ ref, inputs }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`GitHub dispatch ${workflowFile} → ${res.status} ${text || res.statusText}`);
  }
}

export async function listWorkflowRuns(
  workflowFile: string,
  perPage: number = 5,
): Promise<WorkflowRun[]> {
  const res = await ghFetch(
    `/actions/workflows/${workflowFile}/runs?per_page=${perPage}`,
  );
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`GitHub runs ${workflowFile} → ${res.status} ${text || res.statusText}`);
  }
  const data = (await res.json()) as RunsResponse;
  return data.workflow_runs || [];
}

export async function getLatestRun(workflowFile: string): Promise<WorkflowRun | null> {
  const runs = await listWorkflowRuns(workflowFile, 1);
  return runs[0] ?? null;
}

// Poll every `intervalMs` until the given workflow run is no longer in_progress/queued.
// Resolves with the final run state. Timeout = 5 min by default.
export async function pollRunUntilDone(
  workflowFile: string,
  startedAfterIso: string,
  intervalMs: number = 5000,
  timeoutMs: number = 5 * 60 * 1000,
): Promise<WorkflowRun | null> {
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    await new Promise(r => setTimeout(r, intervalMs));
    const runs = await listWorkflowRuns(workflowFile, 5);
    const candidate = runs.find(r => new Date(r.created_at).getTime() >= new Date(startedAfterIso).getTime() - 2000);
    if (!candidate) continue;
    if (candidate.status === 'completed') return candidate;
  }
  return null;
}

export function durationSeconds(run: WorkflowRun): number | null {
  const started = run.run_started_at || run.created_at;
  if (!started || run.status !== 'completed') return null;
  return Math.round((new Date(run.updated_at).getTime() - new Date(started).getTime()) / 1000);
}
